import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";

// We only render the columns the user asked for.

const formatDateTime = (timeString) => {
  if (!timeString) return "-";

  // If it's just "HH:mm:ss", we prepend a dummy date to make it a valid Date object
  // Otherwise, new Date() might return 'Invalid Date'
  const isOnlyTime = /^\d{2}:\d{2}/.test(timeString);
  const date = isOnlyTime
    ? new Date(`1970-01-01T${timeString}`)
    : new Date(timeString);

  if (isNaN(date.getTime())) return timeString; // Fallback if parsing fails

  return date.toLocaleString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const getRecordingUrl = (log) =>
  log.recordingUrl || log.location || log.recording_url || log.blobUrl || log.audioUrl || null;

const getDirectionIcon = (type) => {
  const t = String(type || "").toLowerCase();
  if (t.includes("inbound") || t.includes("incoming")) return <CallReceivedIcon sx={{ fontSize: 16, color: "#10b981" }} />;
  return <CallMadeIcon sx={{ fontSize: 16, color: "#3b82f6" }} />;
};

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "-";
  const s = Number(seconds);
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const CallLogsTable = ({ logs = [] }) => {
  if (!logs.length) return null;

  return (
    <TableContainer
      component={Paper}
      sx={{
        backgroundColor: "white",
        borderRadius: 3,
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f8fafc" }}>
            <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#64748b" }}>TYPE</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#64748b" }}>TIMESTAMP</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#64748b" }}>AGENT</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#64748b" }}>MOBILE</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#64748b" }}>STATUS</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#64748b" }}>OUTCOME</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#64748b" }}>COMMENT</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#64748b" }}>RECORDING</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {logs.map((log, i) => {
            const agent = log.user || "-";
            const callDate = log.callDate;
            const callTime = log.calls_time;
            const disposition = log.sub_disposition || "-";
            const childDisposition = log.childDisposition || "-";
            const status = log.status || log.callStatus || "-";
            const comment = log.comment || "-";
            const recording = getRecordingUrl(log);

            const statusLower = String(status).toLowerCase();

            return (
              <TableRow key={i} hover sx={{ "&:hover": { bgcolor: "rgba(0,0,0,0.02)" } }}>
                <TableCell>
                  <Tooltip title={log.calls_direction || log.callType || "Call"}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {getDirectionIcon(log.calls_direction || log.callType)}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                    {callTime ? formatDateTime(callTime) : "-"}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: 10, color: "#94a3b8", display: "block" }}>
                    {callDate || "-"}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 500 }}>{agent}</TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 500, color: "#475569" }}>
                  {log.mobile || "-"}
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: "#475569" }}>
                  {status}
                </TableCell>
                <TableCell sx={{ fontSize: 11 }}>
                  <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>
                    {disposition}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: 11,
                    maxWidth: 180,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    color: "#64748b"
                  }}
                  title={String(comment)}
                >
                  {comment}
                </TableCell>
                <TableCell>
                  {recording && recording !== "NA" ? (
                    <Box sx={{ width: 380 }}>
                      <audio
                        controls
                        style={{
                          width: "100%",
                          height: "28px",
                        }}
                      >
                        <source src={recording} type="audio/mpeg" />
                      </audio>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">No Record</Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CallLogsTable;
